import "./fileUploader.scss";
import toast from "react-hot-toast";
import { EventSourcePolyfill } from "event-source-polyfill";
import { useOutletContext } from "react-router-dom";

import { useRef, useCallback, useState, useEffect } from "react";
import { useVideoUpload } from "./VideoUploadContext";

export const FileUploader = ({
  identifier,
  onUpload,
  editingMode,
  initialUrls,
  uploadType = "video",
}) => {
  const { setIsLongRunningTask } = useOutletContext();
  const { uploadStates, updateUploadState } = useVideoUpload();
  const [previewUrls, setPreviewUrls] = useState(initialUrls || {});
  const [removedAssets, setRemovedAssets] = useState({});
  const uploadState = uploadStates[identifier] || { status: "idle" };

  // File Refs
  const videoRef = useRef(null);
  const posterRef = useRef(null);
  const pdfRef = useRef(null);
  const sseRef = useRef(null);

  const MAX_SIZE_MB = 10000;
  const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/ogg"];

  // Validate file inputs
  const validateFiles = (videoFile) => {
    if (!videoFile && !editingMode) return "Main video file is required.";
    if (videoFile && !ALLOWED_VIDEO_TYPES.includes(videoFile.type))
      return "Unsupported video type.";
    if (videoFile && videoFile.size > MAX_SIZE_MB * 1024 * 1024)
      return `Video file too large (max ${MAX_SIZE_MB}MB).`;
    return null;
  };

  useEffect(() => {
    if (initialUrls) {
      // Normalize keys
      const normalized = {
        videoUrl: initialUrls.videoUrl || initialUrls.fullUrl,
        posterUrl: initialUrls.posterUrl,
        thumbUrl: initialUrls.thumbUrl,
        previewUrl: initialUrls.previewUrl,
        resources: initialUrls.resources || initialUrls.pdfUrls || [],
      };
      setPreviewUrls(normalized);
    }
  }, [initialUrls]);

  // Handle removing assets
  const handleRemoveAsset = (key, index = null) => {
    setRemovedAssets((prev) => ({ ...prev, [key]: true }));

    setPreviewUrls((prev) => {
      const updated = { ...prev };
      if (Array.isArray(updated[key]) && index !== null) {
        updated[key] = updated[key].filter((_, i) => i !== index);
        if (updated[key].length === 0) delete updated[key];
      } else {
        delete updated[key];
      }
      return updated;
    });

    if (index !== null) {
      onUpload({ [key]: { removedIndex: index } });
    } else {
      onUpload({ [key]: null, removed: true });
    }
  };

  // Unified upload handler
  const handleCompleteUpload = useCallback(async () => {
    const videoFile = videoRef.current?.files?.[0];
    const poster = posterRef.current?.files?.[0];
    const pdfFiles = pdfRef.current?.files;

    // ✅ Only validate when uploading videos
    if (uploadType !== "plan") {
      const validationError = validateFiles(videoFile);
      if (validationError) {
        toast.error(validationError);
        updateUploadState(identifier, {
          status: "error",
          message: validationError,
        });
        return;
      }
    }

    updateUploadState(identifier, {
      status: "uploading",
      progress: 0,
      stage: "Starting upload...",
    });
    setIsLongRunningTask(true);

    try {
      const formData = new FormData();
      if (videoFile) formData.append("videoFile", videoFile);
      if (poster) formData.append("poster", poster);
      if (pdfFiles?.length) {
        for (let i = 0; i < pdfFiles.length; i++) {
          formData.append("resources", pdfFiles[i]);
        }
      }
      //"http://localhost:3500/api/uploads/upload-video",
      const res = await fetch(
        "https://www.enotesunlimited.com/api/uploads/upload-video",
        {
          method: "POST",
          body: formData,
        }
      );

      const { uploadId } = await res.json();
      // `http://localhost:3500/api/uploads/progress/${uploadId}`
      const sse = new EventSourcePolyfill(
        `https://www.enotesunlimited.com/api/uploads/progress/${uploadId}`
      );
      sseRef.current = sse;

      sse.onmessage = (e) => {
        const data = JSON.parse(e.data);

        if (data.progress !== undefined) {
          updateUploadState(identifier, {
            progress: Math.min(100, Math.round(data.progress)),
          });
        }

        if (data.message) {
          updateUploadState(identifier, { stage: data.message });
        }

        if (data.done) {
          if (data.error) {
            updateUploadState(identifier, {
              status: "error",
              stage: "Error ❌",
              message: data.error,
            });
            toast.error(data.error);
          } else {
            // ✅ match backend keys
            // const { videoUrl, posterUrl, resources } = data.video;
            const { videoUrl, previewUrl, thumbUrl, posterUrl, resources } =
              data.video;

            const updated = {
              videoUrl,
              previewUrl,
              thumbUrl,
              posterUrl,
              resources,
            };

            updateUploadState(identifier, {
              status: "done",
              stage: "Completed ✅",
              urls: updated,
            });

            setPreviewUrls(updated);
            onUpload(updated);
          }

          sse.close();
          setIsLongRunningTask(false);
        }
      };

      sse.onerror = () => {
        updateUploadState(identifier, {
          status: "error",
          stage: "Connection error ❌",
          message: "Upload failed due to connection error.",
        });
        toast.error("Upload failed due to connection error.");
        sse.close();
        setIsLongRunningTask(false);
      };
    } catch (err) {
      updateUploadState(identifier, {
        status: "error",
        stage: "Error ❌",
        message: err.message,
      });
      toast.error(err.message || "Upload failed");
      setIsLongRunningTask(false);
    }
  }, [identifier, onUpload, updateUploadState, setIsLongRunningTask]);

  // Cancel ongoing upload
  const cancelUpload = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.close();
      updateUploadState(identifier, {
        status: "cancelled",
        stage: "Cancelled ⚠️",
        message: "Upload cancelled.",
      });
      toast("Upload cancelled", { icon: "⚠️" });
      setIsLongRunningTask(false);
    }
  }, [identifier, updateUploadState, setIsLongRunningTask]);

  // Render preview cards
  const renderPreviewCard = (label, type, urlOrList, ref, key) => {
    if (!urlOrList || (Array.isArray(urlOrList) && urlOrList.length === 0))
      return null;

    return (
      <div className="preview-card">
        <h5>{label}</h5>

        {type === "video" && <video controls src={urlOrList} />}
        {type === "image" && <img src={urlOrList} alt={label} />}
        {type === "files" && (
          <ul>
            {urlOrList.map((link, i) => (
              <li key={i}>
                <a href={link} target="_blank" rel="noopener noreferrer">
                  PDF {i + 1}
                </a>
                <button type="button" onClick={() => handleRemoveAsset(key, i)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="preview-actions">
          {type !== "files" && (
            <button type="button" onClick={() => ref?.current?.click()}>
              Replace
            </button>
          )}
          <button type="button" onClick={() => handleRemoveAsset(key)}>
            Remove
          </button>
          {type === "files" && (
            <button type="button" onClick={() => ref?.current?.click()}>
              Add More
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="video-uploader-container">
      {/* ✅ Only show Video & Poster if not a plan upload */}
      {uploadType !== "plan" && (
        <>
          <div className="input-group">
            <label>
              Video File {editingMode ? "(optional)" : "(required)"}
            </label>
            <input
              type="file"
              ref={videoRef}
              accept={ALLOWED_VIDEO_TYPES.join(",")}
            />
          </div>

          <div className="input-group">
            <label>Poster Image (optional)</label>
            <input type="file" ref={posterRef} accept="image/*" />
          </div>
        </>
      )}

      {/* ✅ Always show PDF input if plan upload */}
      {(uploadType === "plan" || uploadType === "video") && (
        <div className="input-group">
          <label>
            {uploadType === "plan"
              ? "Upload PDF(s)"
              : "PDF Resources (optional)"}
          </label>
          <input type="file" ref={pdfRef} accept="application/pdf" multiple />
        </div>
      )}
      {/* <div className="input-group">
        <label>PDF Resources (optional)</label>
        <input type="file" ref={pdfRef} accept="application/pdf" multiple />
      </div> */}

      <button
        className="upload-button"
        onClick={handleCompleteUpload}
        disabled={uploadState.status === "uploading"}
      >
        {uploadState.status === "uploading" ? "Uploading..." : "Start Upload"}
      </button>

      {uploadState.status === "uploading" && (
        <div className="progress-wrapper">
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${uploadState.progress}%` }}
            />
            <span className="progress-percentage">{uploadState.progress}%</span>
          </div>
          {uploadState.stage && (
            <p className="stage-msg">{uploadState.stage}</p>
          )}
          <button className="cancel-btn" onClick={cancelUpload}>
            Cancel Upload
          </button>
        </div>
      )}

      {(uploadState.status === "done" ||
        Object.keys(previewUrls).length > 0) && (
        <div className="video-preview-container">
          <h4>
            {uploadState.status === "done"
              ? "Uploaded Video Details"
              : "Existing Video Details"}
          </h4>

          <div className="video-preview-grid">
            {renderPreviewCard(
              "Video",
              "video",
              previewUrls.videoUrl,
              videoRef,
              "videoFile"
            )}
            {renderPreviewCard("Video", "video", previewUrls.previewUrl)}
            {renderPreviewCard(
              "Poster",
              "image",
              previewUrls.posterUrl,
              posterRef,
              "posterUrl"
            )}
            {renderPreviewCard(
              "PDFs",
              "files",
              previewUrls.resources,
              pdfRef,
              "resources"
            )}
          </div>
        </div>
      )}
    </div>
  );
};
